/**
 * Employee Portal Layout Component
 * Matches the styling and features of the admin EMS layout
 */
import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer as MuiDrawer,
  List,
  ListItem,
  ListItemButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  CSSObject,
  Theme,
  styled,
  useMediaQuery,
  Fade,
  Breadcrumbs,
  Link,
  Badge,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  ChevronRight,
  ChevronLeft,
  Home,
  LayoutDashboard,
  User,
  Clock,
  Calendar,
  CheckSquare,
  Bell,
  Users,
  LogOut,
  Moon,
  Sun,
  Monitor,
  Mail,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useThemeMode } from '../../context/ThemeContext';
import GradientBackground from '../common/GradientBackground';
import NotificationDropdown from '../notifications/NotificationDropdown';
import { useNotifications } from '../../hooks/useNotifications';
import { Notification } from '../../types/notification';

// --- Configuration ---
const drawerWidth = 200;
const collapsedWidth = 72;
const waterFlowTransition = 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1), top 0.5s cubic-bezier(0.4, 0, 0.2, 1), height 0.5s cubic-bezier(0.4, 0, 0.2, 1), transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.5s cubic-bezier(0.4, 0, 0.2, 1), padding 0.5s cubic-bezier(0.4, 0, 0.2, 1)';

// --- Drawer Mixins ---
const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  top: '80px',
  height: 'calc(100vh - 100px)',
  borderRadius: '16px',
  transform: 'translateY(0)',
  transition: waterFlowTransition,
  overflowX: 'hidden',
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255, 255, 255, 0.06)'
    : '1px solid rgba(0, 0, 0, 0.04)',
  background: theme.palette.mode === 'dark'
    ? 'rgba(28, 28, 30, 0.95)'
    : 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0px 4px 16px -4px rgba(0, 0, 0, 0.3)'
    : '0px 4px 20px -4px rgba(0, 0, 0, 0.06)',
});

const closedMixin = (theme: Theme): CSSObject => ({
  width: collapsedWidth,
  top: '50%',
  height: 'auto',
  paddingBottom: '8px',
  paddingTop: '8px',
  borderRadius: '14px',
  transform: 'translateY(-50%)',
  transition: waterFlowTransition,
  overflowX: 'hidden',
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255, 255, 255, 0.06)'
    : '1px solid rgba(0, 0, 0, 0.04)',
  background: theme.palette.mode === 'dark'
    ? 'rgba(28, 28, 30, 0.95)'
    : 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0px 4px 16px -4px rgba(0, 0, 0, 0.3)'
    : '0px 4px 20px -4px rgba(0, 0, 0, 0.06)',
});

// --- Styled Components ---
const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    '& .MuiDrawer-paper': {
      position: 'fixed',
      left: 0,
      marginLeft: theme.spacing(1.5),
      zIndex: 10,
      ...(open && openedMixin(theme)),
      ...(!open && closedMixin(theme)),
    },
  }),
);

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginTop: '60px',
  minHeight: 'calc(100vh - 60px)',
  overflow: 'hidden',
  transition: waterFlowTransition,
  marginLeft: !open ? -75 : collapsedWidth,
  width: `calc(100% - ${open ? drawerWidth + 20 : collapsedWidth + 20}px)`,
}));

const EmployeeLayout: React.FC = () => {
  const isSmallScreen = useMediaQuery('(max-width:768px)');
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [themeAnchor, setThemeAnchor] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastNotification, setToastNotification] = useState<Notification | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { themeMode, setThemeMode, effectiveTheme } = useThemeMode();

  // Use notifications hook with adaptive polling and toast notifications
  const {
    unreadCount,
  } = useNotifications({
    enablePolling: true,
    limit: 10,
    onNewNotification: (notification) => {
      // Show toast notification for new items
      setToastNotification(notification);
      setToastOpen(true);
    }
  });

  // Handle toast close
  const handleToastClose = () => {
    setToastOpen(false);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => setProfileAnchor(event.currentTarget);
  const handleThemeMenuOpen = (event: React.MouseEvent<HTMLElement>) => setThemeAnchor(event.currentTarget);
  const handleProfileMenuClose = () => setProfileAnchor(null);
  const handleThemeMenuClose = () => setThemeAnchor(null);

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    navigate('/login');
  };

  const handleThemeChange = (mode: 'light' | 'dark' | 'system') => {
    setThemeMode(mode);
    handleThemeMenuClose();
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Employee Menu Items
  const menuItems = [
    { text: 'Dashboard', icon: LayoutDashboard, path: '/employee/dashboard' },
    { text: 'Profile', icon: User, path: '/employee/profile' },
    { text: 'Timesheets', icon: Clock, path: '/employee/timesheets' },
    { text: 'Leave', icon: Calendar, path: '/employee/leave' },
    { text: 'Approvals', icon: CheckSquare, path: '/employee/approvals' },
    { text: 'Service Requests', icon: Mail, path: '/employee/service-requests' },
    { text: 'Organization', icon: Users, path: '/employee/organization' },
    { text: 'Notifications', icon: Bell, path: '/employee/notifications' },
  ];

  // Generate breadcrumbs
  const getBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter(x => x);
    const pathLabels: Record<string, string> = {
      'employee': 'Employee Portal',
      'dashboard': 'Dashboard',
      'profile': 'Profile',
      'timesheets': 'Timesheets',
      'leave': 'Leave Management',
      'approvals': 'Approvals',
      'service-requests': 'Service Requests',
      'organization': 'Organization',
      'notifications': 'Notifications',
    };

    const breadcrumbItems: { label: string; path: string; isLast: boolean }[] = [];

    pathnames.forEach((segment, index) => {
      if (segment === 'employee' || segment === 'dashboard') {
        return;
      }

      const path = `/${pathnames.slice(0, index + 1).join('/')}`;
      const label = pathLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
      const isLast = index === pathnames.length - 1;

      breadcrumbItems.push({ label, path, isLast });
    });

    return breadcrumbItems;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <GradientBackground />

      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          height: 60,
          bgcolor: effectiveTheme === 'dark' ? 'rgba(26, 26, 26, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: effectiveTheme === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)',
          boxShadow: 'none',
        }}
      >
        <Toolbar sx={{ height: 60, px: 2.5, minHeight: '60px !important' }}>
          <Box
            component="img"
            src={isSmallScreen ? '/DatafactZ logo.png' : (effectiveTheme === 'light' ? '/Datafact Z New Logo Dark.png' : '/Datafact Z New Logo.png')}
            alt="DataFactz"
            sx={{
              height: '1.75rem',
              mr: 3,
              ml: 2,
              cursor: 'pointer',
              transition: 'opacity 0.2s ease',
              '&:hover': { opacity: 0.8 },
            }}
            onClick={() => navigate('/employee/dashboard')}
          />

          {/* Divider */}
          <Box
            sx={{
              width: '1px',
              height: '24px',
              bgcolor: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
              mr: 3,
            }}
          />

          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              color: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : '#1D1D1F',
              flexGrow: 1,
              fontWeight: 500,
              fontSize: '1rem',
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              letterSpacing: '-0.01em',
            }}
          >
            {isSmallScreen ? 'Portal' : 'Employee Portal'}
          </Typography>

          {isSmallScreen && <Box sx={{ flexGrow: 1 }} />}

          {/* Theme Toggle */}
          <IconButton
            onClick={handleThemeMenuOpen}
            sx={{
              mr: 1,
              color: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)',
              width: 36,
              height: 36,
              '&:hover': {
                bgcolor: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            {effectiveTheme === 'dark' ? <Moon size={18} strokeWidth={1.75} /> : <Sun size={18} strokeWidth={1.75} />}
          </IconButton>

          {/* Notification Bell with Badge */}
          <IconButton
            onClick={(e) => setNotificationAnchor(e.currentTarget)}
            sx={{
              mr: 1,
              color: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)',
              width: 36,
              height: 36,
              '&:hover': {
                bgcolor: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Badge badgeContent={unreadCount || 0} color="error">
              <Bell size={18} strokeWidth={1.75} />
            </Badge>
          </IconButton>
          <NotificationDropdown
            open={Boolean(notificationAnchor)}
            anchorEl={notificationAnchor}
            onClose={() => setNotificationAnchor(null)}
          />

          {/* Profile Avatar */}
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={handleProfileMenuOpen}>
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: 'primary.main',
                fontWeight: 600,
                fontSize: '0.875rem',
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                color: 'primary.contrastText',
              }}
            >
              {user?.first_name?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer variant="permanent" open={drawerOpen && !isSmallScreen}>
        {/* Toggle Button Inside Drawer */}
        {!isSmallScreen && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: drawerOpen ? 'flex-end' : 'center', py: 1.5, px: drawerOpen ? 1.5 : 0, width: '100%' }}>
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                bgcolor: effectiveTheme === 'dark'
                  ? 'rgba(255, 255, 255, 0.04)'
                  : 'rgba(0, 0, 0, 0.03)',
                color: effectiveTheme === 'dark'
                  ? 'rgba(255, 255, 255, 0.5)'
                  : '#9CA3AF',
                width: 32,
                height: 32,
                borderRadius: '8px',
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: effectiveTheme === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.06)',
                  color: effectiveTheme === 'dark'
                    ? 'rgba(255, 255, 255, 0.8)'
                    : '#6B7280',
                }
              }}
            >
              {drawerOpen ? <ChevronLeft size={16} strokeWidth={1.75} /> : <ChevronRight size={16} strokeWidth={1.75} />}
            </IconButton>
          </Box>
        )}

        <Fade in={true} timeout={300}>
          <List sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, width: '100%', px: 0.75, pt: 0.5 }}>
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              const isOpen = drawerOpen && !isSmallScreen;

              return (
                <ListItem key={item.text} disablePadding sx={{ display: 'block', mb: 0.25 }}>
                  <ListItemButton
                    onClick={() => navigate(item.path)}
                    sx={{
                      minHeight: isOpen ? 44 : 44,
                      width: isOpen ? '90%' : '44px',
                      height: isOpen ? 'auto' : '44px',
                      mx: 'auto',
                      justifyContent: isOpen ? 'flex-start' : 'center',
                      alignItems: 'center',
                      borderRadius: '10px',
                      background: isActive
                        ? (effectiveTheme === 'dark'
                            ? 'rgba(249, 115, 22, 0.15)'
                            : 'rgba(249, 115, 22, 0.1)')
                        : 'transparent',
                      color: isActive
                        ? '#F97316'
                        : (effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.6)' : '#6B7280'),
                      boxShadow: 'none',
                      transition: 'all 0.15s ease',
                      px: isOpen ? 2 : 0,
                      py: isOpen ? 0.75 : 0,
                      '&:hover': {
                        background: isActive
                          ? (effectiveTheme === 'dark'
                              ? 'rgba(249, 115, 22, 0.2)'
                              : 'rgba(249, 115, 22, 0.15)')
                          : (effectiveTheme === 'dark'
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(0, 0, 0, 0.03)'),
                        color: isActive ? '#F97316' : (effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#4B5563'),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isOpen ? 'flex-start' : 'center',
                        width: '100%',
                        color: 'inherit',
                      }}
                    >
                      <Icon size={18} strokeWidth={1.75} />
                      {isOpen && (
                        <Typography
                          sx={{
                            ml: 2,
                            fontWeight: isActive ? 600 : 500,
                            fontSize: '0.875rem',
                            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                            letterSpacing: '-0.01em',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            transition: 'opacity 0.15s ease',
                          }}
                        >
                          {item.text}
                        </Typography>
                      )}
                    </Box>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Fade>
      </Drawer>

      {/* Main Content */}
      <Main open={drawerOpen && !isSmallScreen}>
        {/* Breadcrumb Navigation */}
        {breadcrumbs.length > 0 && (
          <Breadcrumbs
            separator={<ChevronRight size={16} strokeWidth={1.75} />}
            aria-label="breadcrumb"
            sx={{
              mb: 2,
              '& .MuiBreadcrumbs-ol': {
                flexWrap: 'nowrap',
              },
              '& .MuiBreadcrumbs-separator': {
                mx: 1,
                color: 'text.secondary',
              },
            }}
          >
            <Link
              underline="hover"
              color="text.secondary"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate('/employee/dashboard');
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.875rem',
                gap: 0.5,
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              <Home size={16} strokeWidth={1.75} />
              Home
            </Link>
            {breadcrumbs.map((crumb) => (
              crumb.isLast ? (
                <Typography
                  key={crumb.path}
                  color="text.primary"
                  sx={{ fontSize: '0.875rem', fontWeight: 600 }}
                >
                  {crumb.label}
                </Typography>
              ) : (
                <Link
                  key={crumb.path}
                  underline="hover"
                  color="text.secondary"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(crumb.path);
                  }}
                  sx={{
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  {crumb.label}
                </Link>
              )
            ))}
          </Breadcrumbs>
        )}
        <Outlet />
      </Main>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={handleProfileMenuClose}
        sx={{ mt: 1 }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {user?.first_name} {user?.last_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/employee/profile'); }} sx={{ gap: 1.5 }}>
          <User size={18} strokeWidth={1.75} /> Profile
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ gap: 1.5 }}>
          <LogOut size={18} strokeWidth={1.75} /> Logout
        </MenuItem>
      </Menu>

      {/* Theme Menu */}
      <Menu
        anchorEl={themeAnchor}
        open={Boolean(themeAnchor)}
        onClose={handleThemeMenuClose}
        sx={{ mt: 1 }}
      >
        <MenuItem onClick={() => handleThemeChange('light')} selected={themeMode === 'light'} sx={{ gap: 1.5 }}>
          <Sun size={18} strokeWidth={1.75} /> Light
        </MenuItem>
        <MenuItem onClick={() => handleThemeChange('dark')} selected={themeMode === 'dark'} sx={{ gap: 1.5 }}>
          <Moon size={18} strokeWidth={1.75} /> Dark
        </MenuItem>
        <MenuItem onClick={() => handleThemeChange('system')} selected={themeMode === 'system'} sx={{ gap: 1.5 }}>
          <Monitor size={18} strokeWidth={1.75} /> System
        </MenuItem>
      </Menu>

      {/* Toast notification for new notifications */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={handleToastClose}
          severity="info"
          variant="filled"
          sx={{
            width: '100%',
            bgcolor: effectiveTheme === 'dark' ? 'rgba(33, 150, 243, 0.95)' : 'primary.main',
            color: 'primary.contrastText'
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            {toastNotification?.title || 'New Notification'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {toastNotification?.message || ''}
          </Typography>
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmployeeLayout;
